import { useState, useEffect } from 'react';
import moment from 'moment';

import { Button, Table, Tag, Typography } from "antd";
import Title from "antd/lib/typography/Title";
import confirm from 'antd/lib/modal/confirm';
import { ExclamationCircleOutlined, FormOutlined, PrinterOutlined } from '@ant-design/icons';

import notifier from 'utils/notifier';
import { ErrorResponse } from "utils/types";
import { groupArrayOfObjectsByKey, sortArrayByKey } from 'utils/helpers';
import { DiagsStatusCode, HealthCondition, SuspectedCondition } from 'utils/enums';

import { GetDiagsResponse, Diags } from 'store/eligibility/hcc/actions';

import DiagnosisHistoryModal from 'components/Eligibility/MemberEligibilityDetailTables/MedicareHCCSection/DiagnosisHistoryModal';

import MedicareHCCTabProps from './Proptypes';

import styles from './MedicareHCCSection.module.scss';

interface HccTableSectionProps {
  title: string;
  dataSource: any[];
  isFetching: boolean;
}

const MedicareHCCSection = (props: MedicareHCCTabProps) => {
  const [isDiagnosisHistoryModalVisible, setIsDiagnosisHistoryModalVisible] = useState(false);
  const [hccSuggestionDataSource, setHccSuggestionDataSource] = useState<any[]>([]);
  const [hccSuspectDataSource, setSuspectedDataSource] = useState<any[]>([]);
  const [diagnosisHistoryDataSource, setDiagnosisHistoryDataSource] = useState<any[]>([]);
  const [hccCode, setHccCode] = useState('');

  //Note: year1, year2, year3 changes every year, so they need to be dynamic hence using moment().year()
  const hccSuggestionColumns = [
    {
      title: "Category",
      key: "RmiStatusDesc",
      dataIndex: "RmiStatusDesc",
      render: (_: string, record: any, __: number) => {
        return (
          <Button
            type="link"
            className="pl-0"
            onClick={() => openDiagnosisHistoryModal(record)}>{record.RmiStatusDesc} <FormOutlined />
          </Button>
        )
      },
      sorter: (a: any, b: any) => a.hccCode.localeCompare(b.hccCode)
    },
    {
      title: "HCC Description",
      key: "hccDescription",
      dataIndex: "hccDescription",
    },
    {
      title: "DX Code",
      key: "diag",
      dataIndex: "diag",
      render: (_: string, record: any) => {        
          return (
            <span>           
              <div>
                {(record.diag3 !== '' && record.diag3 !== null) ? record.diag3 : ""}
              </div>
              <div>              
                {(record.diag2 !== '' && record.diag2 !== null) ? record.diag2 : ""}
              </div>
              <div>
                {(record.diag1 !== '' && record.diag1 !== null) ? record.diag1 : ""}
              </div> 
            </span>           
          );        
      },
      sorter: (a: any, b: any) => a.dxCode.localeCompare(b.dxCode)
    },
    {
      title: "DOS",
      key: "dos",
      dataIndex: "dos",
      render: (_: string, record: any) => {        
          return (
            <span>           
              <div>
                {(record.dos3 !== '' && record.dos3 !== 'Expected' && record.dos3 !== null) ? record.dos3 : ""}
              </div>
              <div>              
                {(record.dos2 !== '' && record.dos2 !== 'Expected' && record.dos2 !== null) ? record.dos2 : ""}
              </div>
              <div>
                {(record.dos1 !== '' && record.dos1 !== 'Expected' && record.dos1 !== null) ? record.dos1 : ""}
              </div> 
            </span>           
          );        
      }
    },
    {
      title: "Reported By",
      key: "reportedBy",
      dataIndex: "reportedBy",
      render: (_: string, record: any) => {        
          return (
            <span>           
              <div>
                {(record.reportedBy3 !== '' && record.reportedBy3 !== null) ? record.reportedBy3 : ""}
              </div>
              <div>              
                {(record.reportedBy2 !== '' && record.reportedBy2 !== null) ? record.reportedBy2 : ""}
              </div>
              <div>
                {(record.reportedBy1 !== '' && record.reportedBy1 !== null) ? record.reportedBy1 : ""}
              </div> 
            </span>           
          );        
      }
    },
  ];

  const hccSuspectColumns = [
    {
      title: "Category",
      key: "RmiStatusDesc",
      dataIndex: "RmiStatusDesc",
      render: (_: string, record: any, __: number) => {
        return (
          <Button
            type="link"
            className="pl-0"
            onClick={() => openDiagnosisHistoryModal(record)}>{record.RmiStatusDesc} <FormOutlined />
          </Button>
        )
      },
      sorter: (a: any, b: any) => a.hccCode.localeCompare(b.hccCode)
    },
    {
      title: "HCC Description",
      key: "hccDescription",
      dataIndex: "hccDescription",
    },
    {
      title: "DX Code",
      key: "diag",
      dataIndex: "diag",
      render: (_: string, record: any) => {        
          return (
            <span>           
              <div>
                {(record.diag3 !== '' && record.diag3 !== null) ? record.diag3 : ""}
              </div>
              <div>              
                {(record.diag2 !== '' && record.diag2 !== null) ? record.diag2 : ""}
              </div>
              <div>
                {(record.diag1 !== '' && record.diag1 !== null) ? record.diag1 : ""}
              </div> 
            </span>           
          );        
      },
      sorter: (a: any, b: any) => a.dxCode.localeCompare(b.dxCode)
    },
    {
      title: "Suspect Evidence",
      key: "comment",
      dataIndex: "comment",
      render: (_: string, record: any) => {        
          return (
            <span>           
              <div>
                {record.comment ? record.comment : ""}
              </div>
            </span>           
          );        
      }
    }
  ];

  useEffect(() => {
    getDiags();
  }, []);

  useEffect(() => {
    getDiags();
  }, [props.measureYear]);

  const openDiagnosisHistoryModal = (record: any) => {
    setHccCode(record.key);
    let hccGapDetailArr = createHccDetailArray(record);
    hccGapDetailArr = hccGapDetailArr.filter((element) => {
      return element !== null;
    });
    setDiagnosisHistoryDataSource(hccGapDetailArr);
    setIsDiagnosisHistoryModalVisible(true);
  }

  const createHccDetailArray = (record: any) => {
    //  use the map method to create the HCC detail array
    return [1, 2, 3].map((num) => {
      let dos = record[`dos${num}`];
      let claimId = record[`claimId${num}`];
      let year = record[`year${num}`];
      let reportedBy = record[`reportedBy${num}`];
      let diag = record[`diag${num}`];
  
      // return early if DOS is null, empty or 'Expected'
      if (!dos || dos === '' || dos === 'Expected') {
        return null;
      }
  
      return {
        RmiHccCode: record.key,
        RmiClaimNo: claimId,
        RmiCompanyId: record.companyId,
        RmiDateOfService: dos,
        RmiDiagCode: diag,
        RmiProvider: reportedBy,
        RmiYear: year
      };
    });
  };

  const isDiagnosisHistoryModalVisibleHandler = () => {
    return setIsDiagnosisHistoryModalVisible(false);
  }

  const getDetails = (companyId: string, membMpi: string, hccCode: string) => {
    return props.getDetails(companyId, membMpi, hccCode)
      .then((data: any[]) => {
        setHccCode(hccCode);
        setDiagnosisHistoryDataSource(data);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  const updateStatusAndComment = (diags: Diags, status: SuspectedCondition) => {
    let confirmMessage = '';
    if (status === SuspectedCondition.AGREE) {
      confirmMessage = `${diags.RmiHccCode} - Setting the status to 'Agree' will move all conditions with this HCC Category Code to Historical Conditions section.`;
    }

    if (status === SuspectedCondition.DISAGREE) {
      confirmMessage = `${diags.RmiHccCode} - Setting the status to 'Disagree' will remove the condition from the Medicare HCC page.`;
    }

    const params = {
      'Comment': diags.RmiComment,
      'SeverityCode': status === SuspectedCondition.AGREE ? HealthCondition.CHRONIC : diags.RmiSeverityCode,
      'StatusCode': status,
      'Uid': diags.RmiUid,
      'UserId': sessionStorage.user_id
    }

    confirm({
      title: 'Are you sure?',
      content: confirmMessage,
      icon: <ExclamationCircleOutlined />,
      okText: "Yes, move it.",
      onOk() {
        props.updateStatusAndComment(params)
          .then(() => {
            notifier.success('Condition updated Successfully.');
            getDiags();
          })
          .catch((err: ErrorResponse) => {
            notifier.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
          });
      },
    });
  }

  const claimDate = (date: string, severity?: string) => {
    if (!date && !severity) return '';
    else if (!date && severity && severity === HealthCondition.CHRONIC) return 'Expected'; //This is used for the 3rd year only
    else if (!date && severity && severity === HealthCondition.SUSPECTED) return 'Suspected'; //This is used for the 3rd year only
    else if (!date && severity && severity === HealthCondition.ACUTE) return 'Informational'; //This is used for the 3rd year only
    else if(!moment(date).isValid()) return date;

    return moment(date).format('MM/DD/YYYY');
  }

  const processItems = (items: any, destinationArray: any, companyId: string, membMpiNo: any ) => {
    items.forEach((item: any) => {
      let dxCode = item.RmiDiagCode10 + "\n";
      destinationArray.push({
        key: hccCode,
        companyId,
        membMpiNo,
        hccCode,
        comment: item.RmiComment,
        RmiStatusDesc: item.RmiStatusDesc,
        hccDescription: item.RmiDescription,
        dxCode,
        dos1: claimDate(item.RmiReportedDt1),
        dos2: claimDate(item.RmiReportedDt2),
        dos3: claimDate(item.RmiReportedDt3),
        reportedBy1: item.RmiReportedBy1,
        reportedBy2: item.RmiReportedBy2,
        reportedBy3: item.RmiReportedBy3,
        claimId1: item.ClaimID1,
        claimId2: item.ClaimID2,
        claimId3: item.ClaimID3,
        year1: item.yr1,
        year2: item.yr2,
        year3: item.yr3,
        diag1: item.diag1,
        diag2: item.diag2,
        diag3: item.diag3,
      });
    });
  };

  const getDiags = () => {
    const companyId = props.eligibilityMemberInfo.RmiCompanyId;
    const membMpiNo = props.eligibilityMemberInfo.RmiMembMpiNo;
    props
      .getDiags(companyId, membMpiNo, props.measureYear)
      .then((response: GetDiagsResponse[]) => {
        const hccSuggestionResults = response;
        const groupedHccSuggestions = groupArrayOfObjectsByKey(
          hccSuggestionResults,
          "RmiHccCode"
        );
        const groupedHccSuggestionResults: any[] = [];
        const groupedHccSuspectResults: any[] = [];

        // Process and filter in one step
        for (const hccCode in groupedHccSuggestions) {
          const openItems = groupedHccSuggestions[hccCode].filter(
            (item: any) => item.RmiStatusDesc === "Open" || item.RmiStatusDesc === "Closed"
          );
          processItems(
            openItems,
            groupedHccSuggestionResults,
            companyId,
            membMpiNo
          );

          const suspectItems = groupedHccSuggestions[hccCode].filter(
            (item: any) => item.RmiStatusDesc === "Suspected"
          );
          processItems(
            suspectItems,
            groupedHccSuspectResults,
            companyId,
            membMpiNo
          );
        }

        const sortedHccSuggestions = sortArrayByKey(
          groupedHccSuggestionResults,
          "key"
        );
        setHccSuggestionDataSource(sortedHccSuggestions);
        setSuspectedDataSource(groupedHccSuspectResults);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(
          err.response?.data?.Message ||
            "Something went wrong, please contact a system administrator."
        );
      });
  };

  /**
   * hide - member info
   * show - hcc tab on the bottom
   */
  const print = () => {
    $(".print-hcc-section, .print-tab-section, .default-no-result, .patient-info").removeClass(`${styles['exclude-print']}`).addClass(`${styles['include-print']}`);
    $(".print-eligibility-member-info, .print-button, .raf-score, .ant-tabs-nav-wrap, .anticon-form, .title, .health-assessment-section, .print-logo").removeClass(`${styles['include-print']}`).addClass(`${styles['exclude-print']}`);
    window.print();
  }

  const HccTableSection: React.FC<HccTableSectionProps> = ({ title, dataSource, isFetching }) => {
    // Determine which columns to use based on the title
    const tableColumns = title === "Suspected HCCs" ? hccSuspectColumns : hccSuggestionColumns;
  
    return (
      <div className={styles['print-hcc-section']}>
        <div className="row custom-mt">
          <div className="col-lg-24 col-md-24 col-sm-24">
            <Title level={5}>{title}</Title>
          </div>
        </div>
  
        <Table
          className="mb-5"
          columns={tableColumns}
          dataSource={dataSource}
          pagination={{ position: ["bottomCenter"], hideOnSinglePage: true }}
          locale={{ emptyText: (
            <p className="default-no-result text-center" style={{color: 'red'}}>
              <i className="fa fa-exclamation-circle mr-1"></i>
              {`There were no ${title.toLowerCase()} received for this member. Please review the member's chart.`}
            </p>
          )}}
          loading={isFetching}
        />
  
        <DiagnosisHistoryModal
          dataSource={diagnosisHistoryDataSource}
          eligibilityMemberInfo={props.eligibilityMemberInfo}
          hccCode={hccCode}
          isModalVisibleHandler={isDiagnosisHistoryModalVisibleHandler}
          isModalVisible={isDiagnosisHistoryModalVisible}
          isFetchingDetails={props.isFetchingDetails}
        />
      </div>
    );
  };  
  
  return (
    <>
      <HccTableSection 
        title="Historical HCCs" 
        dataSource={hccSuggestionDataSource} 
        isFetching={props.isFetchingDiags}
      />
      <HccTableSection 
        title="Suspected HCCs"
        dataSource={hccSuspectDataSource} 
        isFetching={props.isFetchingDiags}
      />
    </>
  );
  
};
export default MedicareHCCSection;
